<script setup lang="ts">
/**
 * Module: die optionalen Features, die man im Dashboard einschaltet — jedes mit
 * kurzem Setup-Wizard und eigener Einstellungsseite. Inhalt spiegelt den echten
 * Funktionsumfang (Feedback, Escalation, Transcripts, Opening hours, Assignment)
 * und die Token-Mechanik: ein Feature-Token pro aktivem Modul.
 *
 * Anatomie im Karten-Vokabular der Seite: fünf eckige Zellen mit Cursor-
 * Spotlight, jede mit einem kleinen, ruhigen Detail statt Deko.
 *
 * Entrance: die Module „schalten sich ein" — Zellen in Leserichtung, das
 * Icon zündet (Scale + Dreh), danach rastet pro Zelle das Detail ein; bei
 * Escalation fährt der Pfeil Tier 1 → Tier 2.
 */
const sectionRef = ref<HTMLElement | null>(null)
useReveal(sectionRef)
useSpotlight(sectionRef)

useEntrance(sectionRef, ({ q, tl }) => {
  tl.from(q('.module-cell'), { opacity: 0, y: 26, duration: 0.8, stagger: 0.09, clearProps: 'transform' }, 0)
  tl.from(q('.module-icon-glyph'), {
    scale: 0.4,
    rotate: -24,
    duration: 0.6,
    ease: 'back.out(2)',
    stagger: 0.09,
    clearProps: 'transform'
  }, 0.2)
  tl.from(q('.module-detail > *'), { opacity: 0, x: -10, duration: 0.45, stagger: 0.06, clearProps: 'transform' }, 0.55)
  tl.from(q('.module-detail-arrow'), { x: -8, opacity: 0, duration: 0.5, ease: 'power2.out', clearProps: 'transform' }, 0.95)
}, { trigger: '.modules-grid' })

interface Module {
  key: string
  icon: string
  name: string
  text: string
}

const modules: Module[] = [
  {
    key: 'feedback',
    icon: 'i-lucide-star',
    name: 'Feedback',
    text: 'Ask for a rating when a ticket closes: stars, thumbs or emoji, with an optional comment. Every rating feeds your statistics and the agent leaderboard.'
  },
  {
    key: 'escalation',
    icon: 'i-lucide-siren',
    name: 'Escalation',
    text: 'Pass a ticket to another support tier: same ticket, same member, new team. Each handover carries a reason, tiers have their own budgets, and the next team gets an arrival card.'
  },
  {
    key: 'transcripts',
    icon: 'i-lucide-file-text',
    name: 'Transcripts',
    text: 'Every closed ticket keeps its full conversation on a transcript page. You decide who can open it, what it shows and where the link goes.'
  },
  {
    key: 'hours',
    icon: 'i-lucide-clock',
    name: 'Opening hours',
    text: 'Schedules per category in your timezone, holidays included. Outside those hours members see a notice, a warning or a hard stop, whichever you choose.'
  },
  {
    key: 'assignment',
    icon: 'i-lucide-user-check',
    name: 'Assignment',
    text: 'Agents claim tickets, you assign them by hand, or Clarify distributes them per category. Tags and thread markers show at a glance who owns what.'
  }
]
</script>

<template>
  <section id="module" ref="sectionRef" class="modules">
    <span class="section-rule" aria-hidden="true" />
    <div class="section-shell">
      <div class="section-head">
        <h2 class="h-section" data-split>Switch on what you need.</h2>
        <p class="copy-muted modules-intro reveal">
          Every module is optional. A short wizard sets it up, then it gets its own
          settings page in the dashboard, with the docs right next to every field.
        </p>
      </div>

      <div class="modules-grid">
        <article
          v-for="m in modules"
          :key="m.key"
          class="card-shell module-cell spot-card entrance"
          :class="`is-${m.key}`"
        >
          <span class="module-icon" aria-hidden="true">
            <UIcon :name="m.icon" class="module-icon-glyph" />
          </span>
          <h3 class="module-name">{{ m.name }}</h3>
          <p class="module-text copy-muted">{{ m.text }}</p>

          <!-- Ein kleines, ruhiges Detail pro Modul — kein Deko-Rauschen -->
          <div class="module-detail" aria-hidden="true">
            <template v-if="m.key === 'feedback'">
              <span class="module-stars">★</span>
              <span class="module-detail-label">rating on close</span>
            </template>
            <template v-else-if="m.key === 'escalation'">
              <span class="module-chip">Tier 1</span>
              <UIcon name="i-lucide-arrow-right" class="size-[13px] module-detail-arrow" />
              <span class="module-chip module-chip-accent">Tier 2 · Billing</span>
            </template>
            <template v-else-if="m.key === 'transcripts'">
              <span class="module-chip module-chip-mono">
                <UIcon name="i-lucide-link" class="size-[12px]" />
                transcripts.clarify.bot/…
              </span>
              <span class="module-detail-label">participants only</span>
            </template>
            <template v-else-if="m.key === 'hours'">
              <span class="module-chip">Mon–Fri · 09:00–18:00</span>
              <span class="module-detail-label">Europe/Berlin</span>
            </template>
            <template v-else>
              <span class="module-chip module-chip-accent">
                <UIcon name="i-lucide-hand" class="size-[12px]" />
                Claim
              </span>
              <span class="module-detail-label">round-robin · 2 per agent</span>
            </template>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<style scoped>
.modules {
  position: relative;
  padding-top: var(--section-gap);
}

.modules-intro {
  font-size: 1.05rem;
}

.modules-grid {
  display: grid;
  grid-template-columns: 1fr;
  margin-top: 56px;
  /* Hairline-Gitter: die Zellen teilen sich ihre Linien (kein Doppelrand) */
  gap: 1px;
  background: var(--nb-hairline);
  border: 1px solid var(--nb-hairline);
}

.module-cell {
  border: 0;
  border-radius: 0;
  padding: 28px 28px 26px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 100%;
}

.module-icon {
  display: inline-grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: var(--radius-sm);
  background: var(--nb-accent-tint);
  border: 1px solid var(--nb-accent-border);
  color: var(--nb-accent-soft);
}

.module-icon-glyph {
  width: 18px;
  height: 18px;
}

.module-name {
  font-size: 1.08rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--nb-ink);
  margin-top: 4px;
}

.module-text {
  font-size: 0.93rem;
  line-height: 1.55;
  flex: 1;
}

.module-detail {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  padding-top: 14px;
  border-top: 1px solid var(--nb-hairline);
  font-size: 0.78rem;
  color: var(--nb-ink-faint);
}

.module-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--nb-hairline-strong);
  color: var(--nb-ink-muted);
  font-size: 0.76rem;
  font-weight: 500;
  white-space: nowrap;
}

.module-chip-accent {
  border-color: var(--nb-accent-border);
  background: var(--nb-accent-tint);
  color: var(--nb-accent-soft);
}

.module-chip-mono {
  font-family: var(--font-mono);
  font-size: 0.7rem;
  letter-spacing: 0.01em;
}

.module-detail-arrow {
  color: var(--nb-ink-faint);
}

.module-detail-label {
  font-size: 0.76rem;
}

.module-stars {
  color: var(--nb-accent-soft);
  letter-spacing: 0.08em;
  font-size: 0.9rem;
}

@media (min-width: 720px) {
  .modules-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1000px) {
  .modules-grid {
    grid-template-columns: repeat(6, 1fr);
  }

  /* 3 + 2: die ersten drei je 2 Spalten, die letzten zwei je 3 — die Reihe
     bleibt voll, ohne dass eine Zelle allein am Rand hängt */
  .module-cell {
    grid-column: span 2;
  }

  .module-cell:nth-child(4),
  .module-cell:nth-child(5) {
    grid-column: span 3;
  }
}
</style>
